import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges, TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {IDocumentTemplate} from '@ispm/test/components/documents-tab/interfaces/document-template.interface';
import {
  DocumentTestData,
  ResourceSearchType
} from '@ispm/test/components/documents-tab/interfaces/document-test-data.interface';
import {DynamicallyAddComponentService} from '@ispm/shared/services/dynamically-add-component.service';
import {TestInfoComponent} from '@ispm/test/components/test-info/test-info.component';
import {ImmunotherapyComponent} from '@ispm/test/components/immunotherapy/immunotherapy.component';
import {TestSummaryComponent} from '@ispm/test/components/test-summary/test-summary.component';
import {CollectionFormComponent} from '@ispm/test/components/collection-form/collection-form.component';
import {DocumentsTabService} from '@ispm/test/services/documents-tab.service';
import {get, has, set, isEmpty} from 'lodash';
import * as _ from 'lodash';
import {FormHandlerService} from "@ispm/dynamic-form/services/form-handler.service";
import {AgregationService} from "@ispm-client/modules/aggregator/services";
import {Icontext} from "@ispm-client/modules/aggregator/interfaces";
import {Subject, Subscription} from "rxjs";
import {TestService} from '@ispm/dashboard/test/services/test.service';
import { ModalLoaderNgjsService } from '@ispm/layout/services/modal-loader-ngjs.service';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';

const EDITABLE_VIEW_ATTRIBUTE = 'editableView';
const FORM_NAME_ATTRIBUTE = 'formKey';
const RELEVANT_RESOURCE_NAME_ATTRIBUTE = 'relevantResourceName';
const RESOURCE_SEARCH_TYPE = 'searchType';
const EDITED_PROPERTIES = 'editedProperties';
const INDEX_ATTRIBUTE = 'index';
const PROPERTY_PATH_ATTRIBUTE = 'propertyPath';
const APPROVE_BUTTON_ATTRIBUTE = 'approveButtonText';
const CANCEL_BUTTON_ATTRIBUTE = 'cancelButtonText';
const TITLE_BUTTON_ATTRIBUTE = 'title';
const ID_ATTRIBUTE = 'id';
const DEFAULT_TEXT_ATTRIBUTE = 'defaultText';
const DATA_FORM_KEY = 'editableDataSaveForm';
const EDIT_FORM_KEY = 'editableViewEditForm';
const DEFAULT_NOTES_SUB_RESOURCE_TYPE = "letter_notes";
const RICH_TEXT_FORM_KEY = 'richFormKey'; // Adding this to allow flexibility to have different form per section current key FORM_NAME_ATTRIBUTE is tight coupled with opening form modal but not data aggregated
const BORDER = 'border: 2px solid #FFDB1F;';

@Component({
  selector: 'gen-document-preview',
  templateUrl: './document-preview.component.html',
  styleUrls: ['./document-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})

export class DocumentPreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('renderedReportDiv', {static: false}) renderedReportDiv: ElementRef;
  @ViewChild('dynamic', {static: true, read: ViewContainerRef}) viewContainerRef: ViewContainerRef;

  private domObserver!: MutationObserver;
  @ViewChild('renderedReportDivHTMLElement', {static: true}) renderedReportDivHTMLElement: ElementRef;
  @Output() toggleAction: EventEmitter<boolean> = new EventEmitter();
  @Input() showEditDocumentsButton: boolean = false;
  @Input() editingIsPermitted: boolean = false;
  @Input() enableFreeStyleEdit: boolean = false;
  @Input()
  set testData(testData: DocumentTestData) {
    this._testData = testData;

    if (!isEmpty(this._testData)) {
      this.loadRenderedDocument(this.selectedDocument, this.testData);
    }
  }

  get testData() {
    return this._testData;
  }

  @Input()
  set selectedDocument(selectedDocument: any) {
    this._selectedDocument = selectedDocument;

    if (this._testData?.revisionStatus !== 'Canceled' && !isEmpty(this._selectedDocument)) {
      this.loadRenderedDocument(this.selectedDocument, this.testData);
    }
  }

  get selectedDocument() {
    return this._selectedDocument;
  }

  @Input('editModeChangeSubject') editModeChangeSubject: Subject<any>;

  public editingMode: boolean = false;
  private _selectedDocument: any = null;
  private _testData: DocumentTestData;
  public loaded: boolean = true;
  public editableView: string;
  public verticalLeftContainerPatientView: boolean;

  public get isDataChanged(): boolean {
    return this.updatedDataTable != null && this.updatedDataTable.data != null
      && Object.keys(this.updatedDataTable.data).length > 0;
  }

  private componentsToAddDynamically = [
    {key: 'testInfo', value: TestInfoComponent},
    {key: 'immunotherapy', value: ImmunotherapyComponent},
    {key: 'testSummary', value: TestSummaryComponent},
    {key: 'dataCollection', value: CollectionFormComponent}
  ];
  private updatedDataTable: any = null;
  private resourceOriginalData: any = null;
  private formMap = new Map();
  private allData: any = {};
  private aggregatorData$: Subscription;
  private dataStorageKey = "allNotes";

  constructor(private dynamicallyAddComponentService: DynamicallyAddComponentService,
              private documentsTabService: DocumentsTabService,
              private ref: ChangeDetectorRef,
              private formHandlerService: FormHandlerService,
              private aggregationService: AgregationService,
              private testService: TestService,
              private _modalLoaderService: ModalLoaderNgjsService,) {
  }

  ngOnInit() {
    this.dynamicallyAddComponentService.setRootViewContainerRef(this.viewContainerRef);
    //this.loadRenderedDocument(this.selectedDocument, this.testData);
    this.upDateFormKey();
    this.prefetchAllData();
    this.verticalLeftContainerPatientView = !!(this.testService
      && this.testService.testConfiguration && this.testService.testConfiguration.VerticalLeftContainerPatientView);

    this.editModeChangeSubject.subscribe(e => {
      this.editModeChanged(e);
    });
  }

  ngAfterViewInit() {
      // Access the DOM element
      const targetNode = this.renderedReportDiv.nativeElement;
      // Initializing MutationObserver to detect changes in DOM
      this.domObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          this.editingModeStatus?
            this.documentsTabService.setRenderedReportDivChange(this.serializeView(this.renderedReportDivHTMLElement))
            : this.documentsTabService.setRenderedReportDivChange(null);
        });
      });
      this.domObserver.observe(targetNode, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

  serializeView(value: ElementRef): string {
    const nestedHtml = value.nativeElement.innerHTML;
    // Extract <style> tags from the nestedHtml
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    let styles = '';
    let cleanedHtml = nestedHtml;
    // Collecting all <style> blocks and remove them from the body content
    let match;
    while ((match = styleRegex.exec(nestedHtml)) !== null) {
      styles += match[0];
    }
    // Removing all <style> tags from nestedHtml for a cleaned body
    cleanedHtml = nestedHtml.replace(styleRegex, '');
    return `
      <!DOCTYPE html>
      <html>
      <head>
          <meta charset="UTF-8"/>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
              .editable {
                  white-space: normal !important;
                  height: 0px !important;
                  border: none !important;
                  padding: 0px !important;
                  pointer-events: none !important;
              }
          </style>
          ${styles}
      </head>
      <body>
          ${cleanedHtml}
      </body>
      </html>
    `;
  }

  get editingModeStatus(){
    return this.editingMode;
  }

  ngOnDestroy(): void {
    if(this.aggregatorData$){
      this.aggregatorData$.unsubscribe();
    }

    this.editModeChangeSubject.next();
    this.editModeChangeSubject.complete();

    if (this.domObserver) {
      this.domObserver.disconnect();
    }
    this.documentsTabService.setRenderedReportDivChange(null);
  }

  private editModeChanged(event) {
    this.editingMode = event;
    // following condition only, for if this.showEditDocumentsButton is true in configuration
    if (this.editingMode !== undefined && !this.editingMode && this.showEditDocumentsButton && this.enableFreeStyleEdit) {
      this.editingMode = true;
      this._modalLoaderService.popupModal('confirm',
        ConfirmModalLoaderPayload.builder()
          .setTitle("warning")
          .setContent("Changes will be lost once you choose to discard. The following changes will not be saved.")
          .setDeclineButtonText("cancel")
          .setApproveButtonText('Ok'),
        'ALERT'
      ).subscribe(response => {
        if (response.payload === 1) {
          this.editingMode = false;
          this.ref.detectChanges();
          this.toggleAction.emit(false);
          this.documentsTabService.setRenderedReportDivChange(null);
          this.loadRenderedDocument(this.selectedDocument, this.testData);
        } else {
          this.toggleAction.emit(true);
        }
      });
    } else if (!this.editingMode) {
      // Change made on behalf of pathways
      if (this._testData?.revisionStatus !== 'Canceled') {
        this.documentsTabService.setRenderedReportDivChange(null);
      }
    }
    this.activateMouseEvents(event);
  }

  private loadRenderedDocument(document: IDocumentTemplate, testData: DocumentTestData) {
    if (!document) {
      this.loaded = true;
      return;
    }
    this.loaded = false;
    // loading document
    this.documentsTabService.generateXhtmlDocument(document, this.testData).subscribe((renderedHtml) => {
      if (!renderedHtml) {
        return;
      }
      else {
        if (renderedHtml.content) {
          const renderedReport = this.b64DecodeUnicode(renderedHtml.content);
          this.renderedReportDiv.nativeElement.innerHTML = renderedReport;
          this.activateMouseEvents(this.editingMode);
        }
        this.loaded = true;
        this.documentsTabService.setDocumentPreviewStatusAsLoaded();
      }
    });
  }

  private b64DecodeUnicode(encodedString: string): string {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(atob(encodedString).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  private activateMouseEvents(editable) {
    const editableElements: HTMLCollectionOf<HTMLElement> = document.getElementsByClassName('editable') as HTMLCollectionOf<HTMLElement>;

    for (let i = 0; i < editableElements.length; i++) {
      if(editable) {
        editableElements[i].setAttribute('style', BORDER + 'background: white; cursor: auto');

        editableElements[i].onpointerout = function () {
          editableElements[i].setAttribute('style', 'background:white; cursor: auto');
        };

        editableElements[i].onpointerover = function () {
          editableElements[i].setAttribute('style', BORDER + 'background:rgba(255, 219, 31, 0.1); cursor: pointer');
        };
      }else{
        editableElements[i].setAttribute('style', 'background:white; cursor: auto');

        editableElements[i].onpointerover = function () {
          editableElements[i].setAttribute('style', 'background:white; cursor: auto');
        };
      }
    }
  }

  handleClick($event) {
    if (this.editingMode && !this.enableFreeStyleEdit) {
      this.handleClickEventCondition($event);
    }else if(!this.editingMode && this.enableFreeStyleEdit && this.showEditDocumentsButton && this.editingIsPermitted){
      this.handleClickEventCondition($event);
    }
  }

  private handleClickEventCondition($event){
    const elem = this.getEditableAncestor($event.target);
    if (elem) {
      const editableView = elem.getAttribute(EDITABLE_VIEW_ATTRIBUTE);
      const formName = elem.getAttribute(FORM_NAME_ATTRIBUTE);
      const relevantResourceName = elem.getAttribute(RELEVANT_RESOURCE_NAME_ATTRIBUTE);
      // default search type is by workflowRequest
      const searchType = elem.getAttribute(RESOURCE_SEARCH_TYPE);
      const propertyPath = elem.getAttribute(PROPERTY_PATH_ATTRIBUTE);
      const richFormKey = elem.getAttribute(RICH_TEXT_FORM_KEY);
      const index = elem.getAttribute(INDEX_ATTRIBUTE);
      //In this case relevantResourceName resource name should be specified in dynamic form data binding.
      if (editableView === 'dynamicForm'   && relevantResourceName === null) {
        const approveButtonText  = elem.getAttribute(APPROVE_BUTTON_ATTRIBUTE);
        const cancelButtonText  = elem.getAttribute(CANCEL_BUTTON_ATTRIBUTE);
        const title = elem.getAttribute(TITLE_BUTTON_ATTRIBUTE);
        if(formName !== null){
          this.createFormModal(formName, approveButtonText, cancelButtonText, title);
        }else{
          const id = elem.getAttribute(ID_ATTRIBUTE);
          const defaultText = elem.getAttribute(DEFAULT_TEXT_ATTRIBUTE);
          this.createFormModalWithDataAggregation( approveButtonText, cancelButtonText, title, id, defaultText,richFormKey);
        }
      }else{
        this.setOverlayDetails(editableView, [formName], relevantResourceName, searchType, propertyPath, index);
      }
    }
  }

  private getEditableAncestor(elem) {
    for (; elem && elem !== document; elem = elem.parentNode) {
      if (!!elem.getAttribute(EDITABLE_VIEW_ATTRIBUTE)) {
        return elem;
      }
    }
    return null;
  }

  private setOverlayDetails(
    editableView: string,
    formsList: string[],
    relevantResourceName: string,
    searchType: ResourceSearchType,
    propertyPath: string = undefined,
    index: string = undefined
  ) {
    if (editableView != null) {
      document.getElementById('modalContainer').style.display = 'block';
      if (relevantResourceName) {
        this.documentsTabService.findResource(relevantResourceName, searchType, this.testData)

          .subscribe((receivedData) => {
            this.resourceOriginalData = receivedData;
            let editableData = receivedData;
            let propertyDescr = '';
            if (propertyPath && has(receivedData, propertyPath)) {
              const data = get(receivedData, propertyPath);
              const indexIsValid = (index && Array.isArray(data) && data.length > Number(index));
              editableData = indexIsValid ? data[index] : data;

              // create property path string
              propertyDescr = propertyPath + (indexIsValid ? '[' + index + ']' : '');
            }

            this.createDynamicComponent(editableView, formsList, relevantResourceName, editableData, propertyDescr);
          });
      }
      else {
        this.createDynamicComponent(editableView, formsList, 'diagnosticOrder', this.testData.currentTest, '');
      }
    }
  }

  private createDynamicComponent(
    editableView: string,
    formsList: string[],
    relevantResourceName: string,
    receivedData: any,
    resourceProperty: string
  ) {
    const componentRef = this.dynamicallyAddComponentService.createDynamicComponent(this.getComponentByViewName(editableView).value);

    if (editableView == 'dataCollection') {
      (<any> componentRef.instance).relevantFormsList = formsList;
      (<any> componentRef.instance).resourceProperty = resourceProperty;
      (<any> componentRef.instance).formData = receivedData;

      // run changes for Chrome
      this.ref.detectChanges();

      this.updatedDataTable = { };
      Object.assign(this.updatedDataTable, { resourceName: relevantResourceName, data: { }});

      (<any> componentRef.instance).formDataUpdatedEvent.subscribe((updatedData: any) => {
        this.updatedDataTable.data = updatedData;
      });
    }
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target) {
    if (target['id'] === 'modalOverlay') {
      this.dismiss();
    }
  }

  updateRelevantResourceAndRenderDocument() {
    if(!this.isDataChanged) {
      return;
    }

    if (this.updatedDataTable != null && this.updatedDataTable.resourceName) {
      const resourcePropertyEmpty =
        this.updatedDataTable.data.resourceProperty === undefined
        || this.updatedDataTable.data.resourceProperty.length === 0;

      // join original data and edited
      const targetData = resourcePropertyEmpty ?
        this.updatedDataTable.data.formData :
        set(this.resourceOriginalData, this.updatedDataTable.data.resourceProperty, this.updatedDataTable.data.formData);
      this.documentsTabService.updateResource(this.updatedDataTable.resourceName, targetData, this.testData)
        .subscribe(() => {
          this.loadRenderedDocument(this.selectedDocument, this.testData);
        });
    }
  }

  dismiss(){
    this.hideModal();
  }

  hideModal(){
    const modal = document.getElementById('modalContainer');
    modal.style.display = 'none';
  }


  updateReport() {
    this.updateRelevantResourceAndRenderDocument();
    this.hideModal();
  }

  private getComponentByViewName(editableView: any) {
    return this.componentsToAddDynamically.find((descr) => {
      return descr.key === editableView;
    });
  }

  private upDateFormKey(){
    const testConfiguration = this.testService.testConfiguration;

    if (testConfiguration && testConfiguration.additionalDataFormsConfig) {
      _.forEach(testConfiguration.additionalDataFormsConfig, config => {
        const subResourceType = (config.subResouceType) ? config.subResouceType : DEFAULT_NOTES_SUB_RESOURCE_TYPE;
        this.formMap.set(config.key, { formId :config.formId, subResourceType: subResourceType});
      });
    }
  }

  private  getContextString(){
    const contextArrayString = "[" +
      "{ \"reference\" : \"$(workflowConfig.resourcesMap.requestedRevision)}\"," +
      " \"resourceType\" : \"WorkflowRevision\" }, " +
      "{ \"reference\" : \"$(tests.diagnosticOrders.order.id)}\"," +
      " \"resourceType\" : \"DiagnosticOrder\" }, " +
      "{ \"reference\" : \"$(workflowConfig.resourcesMap.workflowRequest)}\"," +
      " \"resourceType\" : \"WorkflowRequest\" } " +
      "]";
    return contextArrayString;
  }


  private prefetchAllData(){

    if(this.formMap.get(DATA_FORM_KEY) !== undefined){
      const bindingForm = this.formMap.get(DATA_FORM_KEY).formId;
      this.formHandlerService.getForm(bindingForm).subscribe(databindingObj => {
        if(has(databindingObj, 'dataBinding')){
          const dataBinding =  databindingObj['dataBinding'];
          if(dataBinding[0] && dataBinding[0].pickFromPath){
            this.dataStorageKey = dataBinding[0].pickFromPath;
            this.aggregatorData$ = this.aggregationService.registerQuery(databindingObj['dataBinding']).subscribe(actualData => {
              this.allData = Object.assign({}, ...actualData);
              this.allData['formId'] = bindingForm;
              this.allData['subResourceType'] = {
                code : this.formMap.get(DATA_FORM_KEY).subResourceType,
                display : this.formMap.get(DATA_FORM_KEY).subResourceType
              };
            });
          }
        }
      });
    }
  }


  private createFormModal(formName, approveButtonText, cancelButtonText, titleText) {
    const currentFormId = this.formMap.get(formName).formId;
    const approveButton = (approveButtonText !== null) ? approveButtonText : 'Update';
    const cancelButton = (cancelButtonText !== null) ? cancelButtonText : 'Cancel';
    const title = (titleText !== null) ? titleText : 'Edit Notes';
    this.formHandlerService.popupDynamicFormModal(
      title,
      currentFormId,
      {},
      approveButton,
      cancelButton
    ).take(1).subscribe(data => {
      if (data['status'] === 1) {
        // This part of context managing should be removed once dynamic form save will take
        // about the context other than patient.

        const contextString = this.getContextString();
        let contextArray:Icontext[] = [];
        this.aggregationService.interPolateWithStore(contextString).then(modifiedContext => {
          const context = JSON.parse(modifiedContext);
          context.forEach(item => {
            contextArray.push(item);
          });
          this.aggregationService.saveFormData(currentFormId, data['payload']['formData'], contextArray).subscribe(savedData => {
            this.loadRenderedDocument(this.selectedDocument, this.testData);
          });
        });
      }
    });
  }


  private createFormModalWithDataAggregation(approveButtonText, cancelButtonText, titleText, attributeId, defaultText,richFormKey) {

    const bindingForm = this.formMap.get(DATA_FORM_KEY).formId;
    const editForm = this.formMap.get(EDIT_FORM_KEY).formId;
    const approveButton = (approveButtonText !== null) ? approveButtonText : 'Update';
    const cancelButton = (cancelButtonText !== null) ? cancelButtonText : 'Cancel';
    const title = (titleText !== null) ? titleText : 'Edit Notes';
    const defaultNote = (defaultText !== null) ? defaultText : '';
    let  currentNote = defaultNote

    if(this.allData[this.dataStorageKey] !== null && this.allData[this.dataStorageKey][attributeId] !== undefined){
      currentNote = this.allData[this.dataStorageKey][attributeId];
    }
    this.formHandlerService.popupDynamicFormModal(
      title,
      richFormKey?richFormKey:editForm,
      { 'notes' : currentNote },
      approveButton,
      cancelButton
    ).subscribe(data => {
      if (data['status'] === 1) {
        // This part of context managing should be removed once dynamic form save will take
        // about the context other than patient.

        const contextString = this.getContextString();
        let contextArray:Icontext[] = [];
        this.aggregationService.interPolateWithStore(contextString).then(modifiedContext => {
          const context = JSON.parse(modifiedContext);
          context.forEach(item => {
            contextArray.push(item);
          });

          let objData = data['payload']['formData'];
          if (objData[this.dataStorageKey]) {
            objData[this.dataStorageKey][attributeId] = objData.notes;
          } else{
            objData[this.dataStorageKey] = {};
            objData[this.dataStorageKey][attributeId] = objData.notes;
          }

          this.allData[this.dataStorageKey] = objData[this.dataStorageKey];
          this.aggregationService.saveFormData(bindingForm, this.allData, contextArray).subscribe(savedData => {
            this.loadRenderedDocument(this.selectedDocument, this.testData);
          });
        });
      }
    });
  }
}
